<script lang="ts">
  import { fa } from '../i18n/fa';
  import Skeleton from './Skeleton.svelte';

  /**
   * The shape of what is coming, so nothing jumps when it arrives (ADR-060). `variant` matches what
   * the place will hold: `lines` a few rows inside a card, `list` a list of records, `table` a data
   * table, `stats` figure cards, `detail` a record's page, `patient` the client file, `day` the
   * day's tiles inside a card, `form` a form, `chart` a chart.
   * `rows` and `columns` follow the real thing where it is known. The label is for screen readers.
   */
  let {
    label = fa.app.loading,
    variant = 'lines',
    rows,
    columns = 4,
  }: {
    label?: string;
    variant?: 'lines' | 'list' | 'table' | 'stats' | 'detail' | 'patient' | 'day' | 'form' | 'chart';
    rows?: number;
    columns?: number;
  } = $props();

  const count = (fallback: number) => Array.from({ length: rows ?? fallback }, (_, i) => i);
  const LINE_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6'];
  const CHART_BARS = ['h-1/2', 'h-3/4', 'h-2/5', 'h-full', 'h-3/5', 'h-4/5', 'h-1/3', 'h-2/3'];
</script>

<div role="status" aria-busy="true" class="loading-soft w-full">
  <span class="sr-only">{label}</span>
  {#if variant === 'lines'}
    <div class="flex flex-col gap-3 py-3">
      {#each count(3) as i (i)}<Skeleton class={LINE_WIDTHS[i % LINE_WIDTHS.length]} />{/each}
    </div>
  {:else if variant === 'list'}
    <div class="flex flex-col gap-4 py-2">
      {#each count(5) as i (i)}
        <div class="flex items-center gap-3">
          <Skeleton class="size-10 shrink-0 rounded-full" />
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton class={`h-3.5 ${LINE_WIDTHS[i % LINE_WIDTHS.length]}`} />
            <Skeleton class="h-3 w-1/3" />
          </div>
        </div>
      {/each}
    </div>
  {:else if variant === 'table'}
    <div class="flex flex-col gap-1 py-2">
      <div class="grid gap-4 pb-3" style:grid-template-columns={`repeat(${columns}, minmax(0, 1fr))`}>
        {#each Array.from({ length: columns }, (_, c) => c) as c (c)}<Skeleton class="h-3 w-2/3" />{/each}
      </div>
      {#each count(6) as i (i)}
        <div class="grid gap-4 border-t border-hairline py-3.5" style:grid-template-columns={`repeat(${columns}, minmax(0, 1fr))`}>
          {#each Array.from({ length: columns }, (_, c) => c) as c (c)}<Skeleton class={`h-3.5 ${LINE_WIDTHS[(i + c) % LINE_WIDTHS.length]}`} />{/each}
        </div>
      {/each}
    </div>
  {:else if variant === 'stats'}
    <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {#each count(4) as i (i)}
        <div class="neu-card flex flex-col gap-3 p-4">
          <Skeleton class="h-3 w-1/2" />
          <Skeleton class="h-7 w-3/4" />
        </div>
      {/each}
    </div>
  {:else if variant === 'detail'}
    <div class="flex flex-col gap-4">
      <div class="neu-card flex items-center gap-4 p-4">
        <Skeleton class="size-14 shrink-0 rounded-full" />
        <div class="flex min-w-0 flex-1 flex-col gap-2.5">
          <Skeleton class="h-5 w-1/3" />
          <Skeleton class="h-3.5 w-1/2" />
        </div>
      </div>
      <div class="neu-card flex flex-col gap-3 p-4">
        {#each count(4) as i (i)}<Skeleton class={LINE_WIDTHS[i % LINE_WIDTHS.length]} />{/each}
      </div>
    </div>
  {:else if variant === 'patient'}
    <div class="flex flex-col gap-4">
      <div class="neu-card flex items-center gap-4 p-4">
        <Skeleton class="size-14 shrink-0 rounded-full" />
        <div class="flex min-w-0 flex-1 flex-col gap-2.5">
          <Skeleton class="h-5 w-40 max-w-full" />
          <Skeleton class="h-3.5 w-56 max-w-full" />
        </div>
        <Skeleton class="hidden h-10 w-28 rounded-md sm:block" />
      </div>
      <Skeleton class="h-12 w-72 max-w-full rounded-md" />
      <div class="grid gap-4 lg:grid-cols-2">
        {#each [0, 1] as i (i)}
          <div class="neu-card flex flex-col gap-3 p-4">
            <Skeleton class="h-4 w-1/3" />
            {#each [0, 1, 2] as k (k)}<Skeleton class={`h-3.5 ${LINE_WIDTHS[(i + k) % LINE_WIDTHS.length]}`} />{/each}
          </div>
        {/each}
      </div>
    </div>
  {:else if variant === 'day'}
    <div class="flex flex-col gap-4 py-1">
      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {#each [0, 1, 2, 3] as i (i)}
          <div class="neu-well flex flex-col gap-2 p-3">
            <Skeleton class="h-3 w-1/2" />
            <Skeleton class="h-6 w-1/3" />
          </div>
        {/each}
      </div>
      {#each count(2) as i (i)}
        <div class="flex items-center justify-between gap-4 border-t border-hairline pt-3">
          <Skeleton class="h-3.5 w-1/4" />
          <Skeleton class="h-3.5 w-1/2" />
        </div>
      {/each}
    </div>
  {:else if variant === 'form'}
    <div class="flex flex-col gap-5 py-2">
      {#each count(4) as i (i)}
        <div class="flex flex-col gap-2">
          <Skeleton class="h-3 w-1/4" />
          <Skeleton class="h-11 rounded-md" />
        </div>
      {/each}
    </div>
  {:else}
    <div class="flex h-56 items-end gap-3 py-2">
      {#each CHART_BARS as bar (bar)}<Skeleton class={`flex-1 ${bar}`} />{/each}
    </div>
  {/if}
</div>
